import { toast } from "react-toastify";
import {
  accessionBandIndexForColumn,
  buildWellLookup,
  normalizeWellKey,
  parseAssignedTestLabels,
  type RunGridRow,
  usesTwoAccessionsPerColumn,
} from "./viewCreateRunHelpers";

type TranslationFn = (value: string) => string;

type PrintRunPlateParams = {
  row: RunGridRow;
  accessionDisplaySlots: string[];
  t: TranslationFn;
};

export const printRunPlate = ({
  row,
  accessionDisplaySlots,
  t,
}: PrintRunPlateParams) => {
  const maxPrintColumnsPerPage = 12;
  const rows = row.rows.length
    ? row.rows
    : ["A", "B", "C", "D", "E", "F", "G", "H"];
  const columns = row.columns.length
    ? row.columns
    : Array.from({ length: 12 }, (_, index) => index + 1);
  const wellLookup = buildWellLookup(row.details);
  const columnCountForBands = Math.max(1, columns.length);
  const isTwoAccessionsPerColumn = usesTwoAccessionsPerColumn(
    rows.length,
    columns.length,
    row.noOfAccession
  );

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const sanitizeAccessionHeader = (value?: string | null) => {
    const trimmed = value?.trim() ?? "";
    if (!trimmed) return "";
    if (/^patient\s*\d+$/i.test(trimmed)) return "";
    return trimmed;
  };

  const buildAccessionHeaderContent = (value?: string | null) => {
    const accessionText = sanitizeAccessionHeader(value);
    if (!accessionText) return "&nbsp;";

    return `<span>${escapeHtml(accessionText)}</span><span class="accession-spacer" aria-hidden="true"></span>`;
  };

  const getPrintCellLabels = (assignTestJson?: string | null) => {
    const seen = new Set<string>();

    return parseAssignedTestLabels(assignTestJson)
      .map((label) => label.trim())
      .filter((label) => {
        if (!label || seen.has(label)) return false;
        seen.add(label);
        return true;
      });
  };

  const columnChunks: number[][] = [];
  for (let i = 0; i < columns.length; i += maxPrintColumnsPerPage) {
    columnChunks.push(columns.slice(i, i + maxPrintColumnsPerPage));
  }

  const buildChunkHeaderCells = (chunkColumns: number[]) => {
    if (chunkColumns.length === 0) return "";

    if (isTwoAccessionsPerColumn) {
      return chunkColumns
        .map((columnNumber) => {
          const firstIndex = (columnNumber - 1) * 2;
          const first = buildAccessionHeaderContent(
            accessionDisplaySlots[firstIndex]
          );
          const second = buildAccessionHeaderContent(
            accessionDisplaySlots[firstIndex + 1]
          );
          return `<th class="patient-cell patient-cell-pair"><div>${first}</div><div class="pair-divider">${second}</div></th>`;
        })
        .join("");
    }

    const groupCells: string[] = [];
    let activeBand: number | null = null;
    let activeSpan = 0;

    const pushActiveBand = () => {
      if (activeBand === null || activeSpan <= 0) return;
      groupCells.push(
        `<th class="patient-cell" colspan="${activeSpan}">${buildAccessionHeaderContent(accessionDisplaySlots[activeBand])}</th>`
      );
    };

    chunkColumns.forEach((columnNumber) => {
      const band = accessionBandIndexForColumn(
        columnNumber,
        columnCountForBands,
        row.noOfAccession
      );
      if (activeBand === null) {
        activeBand = band;
        activeSpan = 1;
        return;
      }
      if (band === activeBand) {
        activeSpan += 1;
        return;
      }
      pushActiveBand();
      activeBand = band;
      activeSpan = 1;
    });

    pushActiveBand();
    return groupCells.join("");
  };

  const pageSections = columnChunks
    .map((chunkColumns, pageIndex) => {
      const headerCells = buildChunkHeaderCells(chunkColumns);
      const numberHeaderCells = chunkColumns
        .map((column) => `<th class="number-cell">${column}</th>`)
        .join("");

      const bodyRows = rows
        .map((rowLabel) => {
          const cellHtml = chunkColumns
            .map((column) => {
              const wellKey = normalizeWellKey(`${rowLabel}${column}`);
              const detail = wellLookup.get(wellKey);
              const testLabels = getPrintCellLabels(detail?.assignTestJson);
              const testHtml = testLabels
                .map(
                  (label, index) =>
                    `<div class="plate-entry">${escapeHtml(label)}${index < testLabels.length - 1 ? "," : ""}</div>`
                )
                .join("");
              return `<td class="plate-cell">${testHtml}</td>`;
            })
            .join("");

          return `<tr><th class="row-cell">${escapeHtml(rowLabel)}</th>${cellHtml}</tr>`;
        })
        .join("");

      const rangeLabel =
        columnChunks.length > 1
          ? `<div class="chunk-label">${t("Columns")} ${chunkColumns[0]}-${chunkColumns[chunkColumns.length - 1]}</div>`
          : "";

      return `
        <section class="print-page ${pageIndex > 0 ? "page-break" : ""}">
          <div class="meta">${t("DATE")}: ____________ &nbsp;&nbsp; ${t("TECH INITIALS")}: ________ &nbsp;&nbsp; ${t("PLATE #")}: __________ &nbsp;&nbsp; ${t("MACHINE NAME")}: ____________ &nbsp;&nbsp; ${t("Lot #")}: ________________</div>
          <div class="panel-title">${escapeHtml((row.runType || "").trim() || "GI PANEL")}</div>
          ${rangeLabel}
          <table>
            <thead>
              <tr>
                <th class="corner-cell"></th>
                ${headerCells}
              </tr>
              <tr>
                <th class="corner-cell"></th>
                ${numberHeaderCells}
              </tr>
            </thead>
            <tbody>
              ${bodyRows}
            </tbody>
          </table>
          <div class="footer">
            <div class="footer-row">${t("PLATE MAPPED BY")}: _______________________ &nbsp;&nbsp; ${t("1ST REVIEWER")}: _______________________ &nbsp;&nbsp; ${t("2ND REVIEWER")}: _______________________</div>
            <div class="footer-row">${t("1ST RESULT UPLOADER")}: _______________________ &nbsp;&nbsp; ${t("2ND RESULT UPLOADER")}: _______________________</div>
            <div class="footer-row">${t("Data Review")}: _______________________ &nbsp;&nbsp; ${t("Exported By")}: _______________________ &nbsp;&nbsp; ${t("Page")} ${pageIndex + 1}/${columnChunks.length}</div>
          </div>
        </section>
      `;
    })
    .join("");

  const printWindow = window.open("", "_blank", "width=1280,height=900");
  if (!printWindow) {
    toast.error(t("Popup blocked. Please allow popups and try again."));
    return;
  }

  printWindow.document.write(`
    <html>
      <head>
        <title>${escapeHtml(row.runName || row.id)}</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 0;
            color: #1f2937;
          }
          .print-page {
            margin: 0;
          }
          .page-break {
            break-before: page;
            page-break-before: always;
          }
          .meta {
            font-size: 12px;
            margin-bottom: 6px;
            white-space: nowrap;
          }
          .panel-title {
            font-size: 13px;
            font-weight: 700;
            margin: 8px 0 6px 0;
          }
          .chunk-label {
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            table-layout: fixed;
          }
          th, td {
            border: 1px solid #111827;
            padding: 4px 6px;
            font-size: 11px;
            vertical-align: middle;
            text-align: center;
          }
          .corner-cell {
            width: 44px;
            min-width: 44px;
          }
          .row-cell {
            width: 44px;
            min-width: 44px;
            font-weight: 700;
          }
          .patient-cell {
            font-weight: 700;
            min-height: 24px;
            font-size: 12px;
          }
          .accession-spacer {
            display: block;
            height: 5.8em;
          }
          .patient-cell-pair {
            padding: 0;
          }
          .patient-cell-pair > div {
            padding: 2px 4px;
            min-height: 16px;
            line-height: 1.2;
          }
          .patient-cell-pair .pair-divider {
            border-top: 1px solid #111827;
          }
          .number-cell {
            font-weight: 700;
          }
          .plate-cell {
            min-height: 28px;
            height: 28px;
            font-size: 9px;
            vertical-align: top;
            white-space: normal;
            word-break: break-word;
          }
          .plate-entry {
            margin-bottom: 1em;
          }
          .footer {
            margin-top: 12px;
            font-size: 12px;
          }
          .footer-row {
            margin-top: 6px;
          }
          .footer-row:first-child {
            margin-top: 0;
          }
        </style>
      </head>
      <body>${pageSections}</body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  printWindow.close();
};
